import { useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { usePageTitle } from '@/hooks/usePageTitle'
import { AppShell3 } from '@/components/app-shell-3'
import { PageTransition } from '@/components/PageTransition'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Smartphone,
  Copy,
  Check,
  Apple,
  Chrome,
  ExternalLink,
  QrCode,
  Link2,
  BookOpen,
  Clock,
  CalendarDays,
  CalendarCheck,
  WifiOff,
  Download,
} from 'lucide-react'

const FEATURES = [
  { title: 'My Training', desc: 'View qualifications, compliance status, and expiry dates', icon: BookOpen },
  { title: 'Clock In/Out', desc: 'Quick check-in with location tracking', icon: Clock },
  { title: 'Leave Requests', desc: 'View balance and submit leave requests', icon: CalendarDays },
  { title: 'My Bookings', desc: 'See your upcoming training bookings', icon: CalendarCheck },
  { title: 'Offline Support', desc: 'Works without internet — syncs when reconnected', icon: WifiOff },
  { title: 'Install as App', desc: 'Add to home screen for native app experience', icon: Download },
]

export default function MobileInstallPage() {
  usePageTitle('Get Mobile App')
  const [copied, setCopied] = useState(false)
  const mobileUrl = window.location.hostname === 'localhost'
    ? 'http://localhost:3222'
    : 'https://mobile.aitrainingmatrix.com'

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)
  const isAndroid = /Android/i.test(navigator.userAgent)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(mobileUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      const textarea = document.createElement('textarea')
      textarea.value = mobileUrl
      document.body.appendChild(textarea)
      textarea.select()
      document.execCommand('copy')
      document.body.removeChild(textarea)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <AppShell3>
      <PageTransition>
        <div className="min-h-screen bg-gray-50">
          {/* Header */}
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-2">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-emerald-100 rounded-xl">
                <Smartphone className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Get the Mobile App</h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  Install the personal app on your phone for quick access to your qualifications, attendance, and check-in.
                </p>
              </div>
            </div>
          </div>

          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
            {/* If on mobile, show direct link */}
            {isMobile ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Smartphone className="h-5 w-5 text-emerald-500" />
                    Install on this device
                  </CardTitle>
                  <CardDescription>
                    Open the link below, then follow the instructions to add it to your home screen.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <a
                    href={mobileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full"
                  >
                    <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open Mobile App
                    </Button>
                  </a>

                  {isIOS && (
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Apple className="h-4 w-4 text-gray-600" />
                        <p className="text-sm font-medium text-gray-700">iOS Instructions</p>
                      </div>
                      <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                        <li>Open the link above in <strong>Safari</strong></li>
                        <li>Tap the <strong>Share</strong> button (square with arrow)</li>
                        <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
                        <li>Tap <strong>"Add"</strong> to confirm</li>
                      </ol>
                    </div>
                  )}

                  {isAndroid && (
                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      <div className="flex items-center gap-2">
                        <Chrome className="h-4 w-4 text-gray-600" />
                        <p className="text-sm font-medium text-gray-700">Android Instructions</p>
                      </div>
                      <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                        <li>Open the link above in <strong>Chrome</strong></li>
                        <li>Tap the <strong>Install</strong> banner at the bottom, or</li>
                        <li>Tap <strong>menu (...)</strong> → <strong>"Install app"</strong> or <strong>"Add to Home screen"</strong></li>
                      </ol>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              /* Desktop: show QR code and share link side by side */
              <div className="grid md:grid-cols-5 gap-6">
                {/* QR Code — wider */}
                <Card className="md:col-span-3">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <QrCode className="h-5 w-5 text-emerald-500" />
                      Scan QR Code
                    </CardTitle>
                    <CardDescription>
                      Scan this with your phone's camera to open the mobile app.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex justify-center py-8">
                    <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                      <QRCodeSVG
                        value={mobileUrl}
                        size={240}
                        level="M"
                        includeMargin={false}
                        fgColor="#10b981"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Share link + instructions */}
                <Card className="md:col-span-2">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Link2 className="h-5 w-5 text-emerald-500" />
                      Share the Link
                    </CardTitle>
                    <CardDescription>
                      Copy the link and send it to your phone via email, message, or any app.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <div className="bg-gray-50 rounded-lg px-3 py-2.5 flex items-center gap-2 border">
                      <code className="text-sm text-gray-700 truncate flex-1">{mobileUrl}</code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCopy}
                        className="flex-shrink-0"
                      >
                        {copied ? (
                          <Check className="h-4 w-4 text-emerald-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>

                    <div className="space-y-3">
                      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                        <div className="flex items-center gap-2">
                          <Apple className="h-4 w-4 text-gray-600" />
                          <p className="text-sm font-medium text-gray-700">iPhone / iPad</p>
                        </div>
                        <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                          <li>Open in <strong>Safari</strong></li>
                          <li>Tap <strong>Share → Add to Home Screen</strong></li>
                        </ol>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                        <div className="flex items-center gap-2">
                          <Chrome className="h-4 w-4 text-gray-600" />
                          <p className="text-sm font-medium text-gray-700">Android</p>
                        </div>
                        <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
                          <li>Open in <strong>Chrome</strong></li>
                          <li>Tap <strong>Install</strong> or <strong>... → Add to Home screen</strong></li>
                        </ol>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Feature Preview */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">What's in the Mobile App?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {FEATURES.map(f => {
                    const Icon = f.icon
                    return (
                      <div key={f.title} className="flex items-start gap-3 bg-gray-50 rounded-lg p-4">
                        <div className="p-2 bg-white rounded-lg border border-gray-200 flex-shrink-0">
                          <Icon className="h-4 w-4 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">{f.title}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{f.desc}</p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </PageTransition>
    </AppShell3>
  )
}
